// client/src/components/pages/AmendmentAnalytics/index.tsx (converted from .js, ST-00221)
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  FaChartBar,
  FaSpinner,
  FaSync,
  FaTicketAlt,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaHourglassHalf,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
} from 'recharts';
import type { AxiosResponse } from 'axios';
import api from '../../../services/api';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import { PageHeader } from '../../../types/commonComponents';
import { useThemeTokens, getToken } from '../../../utils/themeTokens';
import { toApiDateString } from '../../../utils/dateUtils';
import type {
  AnalyticsRange,
  AnalyticsSummary,
  FieldBreakdownRow,
  LocationBreakdownRow,
  TimeToAmend,
  TrendRow,
  UserBreakdownRow,
} from '../../../types/clientPages/amendmentAnalytics';

const PALETTE = {
  teal: ['#45B7AA', '#3da99d', '#2d8a80', '#1c6b63'],
  accents: [
    '#45B7AA',
    '#6366f1',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#ec4899',
    '#14b8a6',
    '#f97316',
    '#64748b',
  ],
  donut: [
    '#45B7AA',
    '#6366f1',
    '#f59e0b',
    '#ec4899',
    '#06b6d4',
    '#8b5cf6',
    '#14b8a6',
    '#f97316',
    '#ef4444',
    '#64748b',
  ],
};

const formatDuration = (hours: number | null | undefined) => {
  if (hours === 0 || hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const hrs = Math.round(hours % 24);
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
};

const formatCompact = (n: number | null | undefined) => {
  if (n == null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
};

interface MetricCardProps {
  icon: ComponentType<{ className?: string; style?: CSSProperties }>;
  label: string;
  value: ReactNode;
  detail?: string;
  accentColor?: string;
  delay?: number;
}

/* ─── Metric Card ─── */
const MetricCard = ({
  icon: Icon,
  label,
  value,
  detail,
  accentColor = '#45B7AA',
  delay = 0,
}: MetricCardProps) => (
  <div
    className="group relative bg-surface rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-primary-300 transition-all duration-300 overflow-hidden"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div
      className="absolute top-0 left-0 right-0 h-[3px] opacity-80"
      style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)` }}
    />
    <div className="p-4 sm:p-5">
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Icon className="w-4 h-4" style={{ color: accentColor }} />
        </div>
      </div>
      <p className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className="text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums tracking-tight font-display">
        {value ?? '—'}
      </p>
      {detail && <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">{detail}</p>}
    </div>
  </div>
);

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  className?: string;
}

/* ─── Chart Card Wrapper ─── */
const ChartCard = ({ title, subtitle, children, className = '' }: ChartCardProps) => (
  <div
    className={`bg-surface rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 ${className}`}
  >
    <div className="px-5 pt-5 pb-3">
      <h3 className="text-sm font-semibold text-slate-800 tracking-tight font-display">{title}</h3>
      {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="px-3 pb-4">{children}</div>
  </div>
);

interface ChartTooltipProps {
  active?: boolean;
  payload?: { color?: string; name?: string; value?: number | string }[];
  label?: string;
}

/* ─── Custom Tooltip ─── */
const ChartTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 dark:bg-slate-50 text-white rounded-lg shadow-xl px-3 py-2 text-xs border border-slate-700 dark:border-slate-200">
      <p className="font-medium text-slate-300 dark:text-slate-600 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-slate-400 dark:text-slate-500">{entry.name}:</span>
          <span className="font-semibold tabular-nums">{entry.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/** What recharts hands the active donut sector. */
interface ActiveShapeProps {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: LocationBreakdownRow;
  percent: number;
  value: number;
}

/* ─── Active Donut Sector ─── */
const renderActiveShape = (props: ActiveShapeProps) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } =
    props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke="none"
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 12}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.3}
        stroke="none"
      />
      <text
        x={cx}
        y={cy - 10}
        textAnchor="middle"
        fill={getToken('--text-strong')}
        className="text-sm font-bold"
        style={{ fontFamily: 'DM Sans, Inter, sans-serif' }}
      >
        {payload.location?.length > 18 ? payload.location.slice(0, 18) + '…' : payload.location}
      </text>
      <text
        x={cx}
        y={cy + 10}
        textAnchor="middle"
        fill={getToken('--text-muted')}
        className="text-xs"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {value.toLocaleString()} ({(percent * 100).toFixed(0)}%)
      </text>
    </g>
  );
};

interface ResponseTimeGaugeProps {
  min: number;
  avg: number;
  max: number;
  sampleSize: number;
}

/* ─── Response Time Gauge ─── */
const ResponseTimeGauge = ({ min, avg, max, sampleSize }: ResponseTimeGaugeProps) => {
  const range = max - min || 1;
  const avgPos = Math.min(100, Math.max(0, ((avg - min) / range) * 100));

  return (
    <div className="space-y-4 px-2">
      <div className="relative pt-6 pb-2">
        <div className="h-2.5 rounded-full bg-gradient-to-r from-emerald-400 via-amber-400 to-red-400 relative overflow-visible">
          <div
            className="absolute -top-5 transform -translate-x-1/2 flex flex-col items-center"
            style={{ left: `${avgPos}%` }}
          >
            <div className="text-[10px] font-bold text-slate-700 bg-surface px-1.5 py-0.5 rounded shadow-sm border border-slate-200 whitespace-nowrap">
              avg {formatDuration(avg)}
            </div>
            <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent border-t-slate-300" />
          </div>
          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-surface border-[3px] border-slate-700 shadow-md transform -translate-x-1/2"
            style={{ left: `${avgPos}%` }}
          />
        </div>
      </div>
      <div className="flex justify-between items-end">
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Fastest</p>
          <p className="text-base font-bold text-emerald-600 tabular-nums">{formatDuration(min)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Average</p>
          <p className="text-xl font-bold text-slate-800 tabular-nums">{formatDuration(avg)}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Slowest</p>
          <p className="text-base font-bold text-red-500 tabular-nums">{formatDuration(max)}</p>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 text-center">
        Measured across {sampleSize?.toLocaleString()} tickets with weighbridge timestamps
      </p>
    </div>
  );
};

/** This month up to yesterday; on the 1st of the month, the whole previous month. */
const defaultRange = (): { start: Date; end: Date } => {
  const today = new Date();
  if (today.getDate() === 1) {
    return {
      start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      end: new Date(today.getFullYear(), today.getMonth(), 0),
    };
  }
  const start = new Date();
  start.setDate(1);
  const end = new Date();
  end.setDate(end.getDate() - 1);
  return { start, end };
};

/* ═══════ Main Component ═══════ */
const AmendmentAnalytics = () => {
  const t = useThemeTokens();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<AnalyticsSummary | null>(null);
  const [fieldBreakdown, setFieldBreakdown] = useState<FieldBreakdownRow[]>([]);
  const [locationBreakdown, setLocationBreakdown] = useState<LocationBreakdownRow[]>([]);
  const [userBreakdown, setUserBreakdown] = useState<UserBreakdownRow[]>([]);
  const [trendData, setTrendData] = useState<TrendRow[]>([]);
  const [responseTimes, setResponseTimes] = useState<TimeToAmend | null>(null);
  const [initial] = useState(defaultRange);
  // What the picker shows
  const [startDate, setStartDate] = useState<Date>(initial.start);
  const [endDate, setEndDate] = useState<Date>(initial.end);
  const [pickerOpen, setPickerOpen] = useState(false);
  // What the page loads. Picking a start date used to reload at once with half a range — 12
  // queries per keystroke or click, the page swapped for a spinner and the picker forced shut
  // (AMD-30, ST-00203). The range is taken over once the picker has closed with both dates.
  const [range, setRange] = useState<AnalyticsRange>(() => ({
    startDate: toApiDateString(initial.start),
    endDate: toApiDateString(initial.end),
  }));
  const [activeDonutIndex, setActiveDonutIndex] = useState(0);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (pickerOpen) return;
    const next = { startDate: toApiDateString(startDate), endDate: toApiDateString(endDate) };
    if (!next.startDate || !next.endDate) return;
    setRange((prev) =>
      prev.startDate === next.startDate && prev.endDate === next.endDate ? prev : next
    );
  }, [pickerOpen, startDate, endDate]);

  const loadAnalytics = useCallback(async () => {
    // Only the newest request may update the page (a slower, older one would overwrite it).
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    const params: AnalyticsRange = { startDate: range.startDate, endDate: range.endDate };

    try {
      const [summaryRes, fieldRes, locationRes, userRes, trendRes, ttaRes]: [
        AxiosResponse<AnalyticsSummary>,
        AxiosResponse<FieldBreakdownRow[]>,
        AxiosResponse<LocationBreakdownRow[]>,
        AxiosResponse<UserBreakdownRow[]>,
        AxiosResponse<TrendRow[]>,
        AxiosResponse<TimeToAmend>,
      ] = await Promise.all([
        api.amendmentAnalytics.summary(params),
        api.amendmentAnalytics.byField(params),
        api.amendmentAnalytics.byLocation(params),
        api.amendmentAnalytics.byUser(params),
        api.amendmentAnalytics.trend(params),
        api.amendmentAnalytics.timeToAmend(params),
      ]);
      if (seq !== requestSeq.current) return;
      setOverview(summaryRes.data);
      setFieldBreakdown(fieldRes.data);
      setLocationBreakdown(locationRes.data);
      setUserBreakdown(userRes.data);
      setTrendData(
        trendRes.data.map((d) => ({
          ...d,
          date: new Date(d.date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }),
        }))
      );
      setResponseTimes(ttaRes.data);
    } catch (caught) {
      if (seq !== requestSeq.current) return;
      const err = caught as Error & { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || err.message || 'Failed to load analytics');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const totalUserAmendments = useMemo(
    () => userBreakdown.reduce((sum, u) => sum + u.amendmentCount, 0),
    [userBreakdown]
  );

  const approvalRate = useMemo(() => {
    if (!overview) return null;
    const approved = overview.approvedCount || 0;
    const total = approved + (overview.rejectedCount || 0);
    return total > 0 ? Math.round((approved / total) * 100) : null;
  }, [overview]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaChartBar}
        title="Amendment Analytics"
        subtitle="Correction patterns, user activity, and operational insights"
        toolbar={
          <div className="sm:w-64">
            {/* Never disabled while loading: that shut the open calendar (AMD-30) */}
            <UnifiedDateRangePicker
              startDate={startDate}
              onStartDateChange={setStartDate}
              endDate={endDate}
              onEndDateChange={setEndDate}
              onOpenChange={setPickerOpen}
            />
          </div>
        }
        actions={
          <button
            type="button"
            onClick={loadAnalytics}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
            className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <FaSync className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            <span className="page-header__label">Refresh</span>
          </button>
        }
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
            <span className="font-medium">Error:</span> {error}
          </div>
        )}

        {!overview ? (
          loading && (
            <div
              role="status"
              className="flex flex-col items-center justify-center py-32 text-slate-400 gap-3"
            >
              <FaSpinner className="w-8 h-8 animate-spin text-primary-400" />
              <p className="text-sm font-medium">Loading analytics...</p>
            </div>
          )
        ) : (
          // Reloads keep the results on screen under an overlay instead of replacing the page
          <div className="relative space-y-6" aria-busy={loading}>
            {loading && (
              <div
                role="status"
                className="absolute inset-0 z-10 flex items-start justify-center pt-24 rounded-xl bg-slate-50/60 backdrop-blur-[1px]"
              >
                <div className="flex items-center gap-2 rounded-full bg-surface border border-slate-200 shadow-md px-4 py-2 text-sm font-medium text-slate-600">
                  <FaSpinner className="w-4 h-4 animate-spin text-primary-500" aria-hidden="true" />
                  Loading analytics…
                </div>
              </div>
            )}
            {/* ─── Metric Cards ─── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <MetricCard
                icon={FaChartBar}
                label="Total Corrections"
                value={formatCompact(overview?.totalAmendments)}
                accentColor="#45B7AA"
                delay={0}
              />
              <MetricCard
                icon={FaTicketAlt}
                label="Tickets Amended"
                value={formatCompact(overview?.uniqueTickets)}
                accentColor="#6366f1"
                delay={50}
              />
              <MetricCard
                icon={FaUsers}
                label="Active Users"
                value={overview?.uniqueUsers}
                accentColor="#06b6d4"
                delay={100}
              />
              <MetricCard
                icon={FaClock}
                label="Avg Response Time"
                value={formatDuration(responseTimes?.avgHours)}
                detail={`across ${responseTimes?.sampleSize?.toLocaleString() || 0} tickets`}
                accentColor="#f59e0b"
                delay={150}
              />
              <MetricCard
                icon={FaHourglassHalf}
                label="Pending Reviews"
                value={overview?.pendingApprovals}
                accentColor="#fb923c"
                delay={200}
              />
              <MetricCard
                icon={FaCheckCircle}
                label="Approval Rate"
                value={approvalRate != null ? `${approvalRate}%` : '—'}
                detail={`${overview?.approvedCount || 0} approved · ${overview?.rejectedCount || 0} rejected`}
                accentColor="#10b981"
                delay={250}
              />
            </div>

            {/* ─── Trend Chart ─── */}
            <ChartCard
              title="Correction Volume Over Time"
              subtitle="Daily amendment and ticket counts"
            >
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trendData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendFillAmendments" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#45B7AA" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#45B7AA" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="trendFillTickets" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.gridSubtle} vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: t.axisLabel }}
                      tickLine={false}
                      axisLine={{ stroke: t.grid }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: t.axisLabel }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="amendmentCount"
                      name="Corrections"
                      stroke="#45B7AA"
                      strokeWidth={2}
                      fill="url(#trendFillAmendments)"
                      dot={false}
                      activeDot={{ r: 4, fill: '#45B7AA', stroke: t.surface, strokeWidth: 2 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="ticketCount"
                      name="Tickets"
                      stroke="#6366f1"
                      strokeWidth={2}
                      fill="url(#trendFillTickets)"
                      dot={false}
                      activeDot={{ r: 4, fill: '#6366f1', stroke: t.surface, strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">
                  No trend data for the selected period
                </p>
              )}
            </ChartCard>

            {/* ─── Two-Column: Fields + Locations ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Field Breakdown (3 cols) */}
              <ChartCard
                title="Most Corrected Fields"
                subtitle="Top fields requiring amendments"
                className="lg:col-span-3"
              >
                {fieldBreakdown.length > 0 ? (
                  <ResponsiveContainer
                    width="100%"
                    height={Math.max(220, Math.min(fieldBreakdown.length, 12) * 32)}
                  >
                    <BarChart
                      data={fieldBreakdown.slice(0, 12)}
                      layout="vertical"
                      margin={{ left: 4, right: 16, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={t.gridSubtle}
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10, fill: t.axisLabel }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        dataKey="field"
                        type="category"
                        width={120}
                        tick={{ fontSize: 10, fill: t.textMuted }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="count" name="Corrections" radius={[0, 6, 6, 0]} barSize={18}>
                        {fieldBreakdown.slice(0, 12).map((_, i) => (
                          <Cell
                            key={i}
                            fill={i === 0 ? '#45B7AA' : i < 3 ? '#45B7AA99' : '#45B7AA55'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-12">
                    No field data available
                  </p>
                )}
              </ChartCard>

              {/* Location Donut (2 cols) */}
              <ChartCard
                title="Corrections by Location"
                subtitle="Click a segment to inspect"
                className="lg:col-span-2"
              >
                {locationBreakdown.length > 0 ? (
                  <div className="flex flex-col items-center">
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          activeIndex={activeDonutIndex}
                          activeShape={(props: unknown) =>
                            renderActiveShape(props as ActiveShapeProps)
                          }
                          data={locationBreakdown.slice(0, 8)}
                          dataKey="amendmentCount"
                          nameKey="location"
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={95}
                          paddingAngle={2}
                          stroke="none"
                          onMouseEnter={(_, index) => setActiveDonutIndex(index)}
                        >
                          {locationBreakdown.slice(0, 8).map((_, i) => (
                            <Cell
                              key={i}
                              fill={PALETTE.donut[i % PALETTE.donut.length]}
                              stroke="none"
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-2 -mt-2">
                      {locationBreakdown.slice(0, 8).map((loc, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1.5 cursor-pointer group"
                          onMouseEnter={() => setActiveDonutIndex(i)}
                        >
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0 group-hover:scale-125 transition-transform"
                            style={{ backgroundColor: PALETTE.donut[i % PALETTE.donut.length] }}
                          />
                          <span className="text-[10px] text-slate-500 group-hover:text-slate-800 transition-colors truncate max-w-[100px]">
                            {loc.location}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-12">
                    No location data available
                  </p>
                )}
              </ChartCard>
            </div>

            {/* ─── Two-Column: Users + Response Time ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* User Activity Table (3 cols) */}
              <ChartCard
                title="User Activity"
                subtitle="Amendment volume by operator"
                className="lg:col-span-3"
              >
                {userBreakdown.length > 0 ? (
                  <div className="overflow-x-auto px-2">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-2.5 px-2 font-semibold text-slate-400 uppercase tracking-wider text-[10px]">
                            Operator
                          </th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-400 uppercase tracking-wider text-[10px]">
                            Corrections
                          </th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-400 uppercase tracking-wider text-[10px]">
                            Tickets
                          </th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-400 uppercase tracking-wider text-[10px] hidden sm:table-cell">
                            Per Ticket
                          </th>
                          <th className="text-left py-2.5 px-2 font-semibold text-slate-400 uppercase tracking-wider text-[10px] hidden md:table-cell">
                            Last Active
                          </th>
                          <th className="py-2.5 px-2 font-semibold text-slate-400 uppercase tracking-wider text-[10px] w-32">
                            Volume
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {userBreakdown.map((entry, i) => {
                          const share =
                            totalUserAmendments > 0
                              ? (entry.amendmentCount / totalUserAmendments) * 100
                              : 0;
                          const isTop = i === 0;
                          return (
                            <tr
                              key={i}
                              className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors group"
                            >
                              <td className="py-2.5 px-2">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                                    style={{
                                      backgroundColor: PALETTE.accents[i % PALETTE.accents.length],
                                    }}
                                  >
                                    {entry.user?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <span
                                    className={`font-medium truncate max-w-[120px] ${isTop ? 'text-slate-900' : 'text-slate-700'}`}
                                  >
                                    {entry.user}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2.5 px-2 text-right tabular-nums font-semibold text-slate-800">
                                {entry.amendmentCount.toLocaleString()}
                              </td>
                              <td className="py-2.5 px-2 text-right tabular-nums text-slate-500">
                                {entry.ticketCount.toLocaleString()}
                              </td>
                              <td className="py-2.5 px-2 text-right tabular-nums text-slate-500 hidden sm:table-cell">
                                {(entry.amendmentCount / entry.ticketCount).toFixed(1)}
                              </td>
                              <td className="py-2.5 px-2 text-slate-400 hidden md:table-cell">
                                {entry.lastAmendment
                                  ? new Date(entry.lastAmendment).toLocaleDateString('en-ZA')
                                  : '—'}
                              </td>
                              <td className="py-2.5 px-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full rounded-full transition-all duration-700"
                                      style={{
                                        width: `${share}%`,
                                        backgroundColor:
                                          PALETTE.accents[i % PALETTE.accents.length],
                                        opacity: isTop ? 1 : 0.6,
                                      }}
                                    />
                                  </div>
                                  <span className="text-[10px] text-slate-400 w-8 text-right tabular-nums font-medium">
                                    {share.toFixed(0)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-12">No user data available</p>
                )}
              </ChartCard>

              {/* Response Time (2 cols) */}
              <ChartCard
                title="Time to First Correction"
                subtitle="Hours from weighbridge to amendment"
                className="lg:col-span-2"
              >
                {responseTimes && responseTimes.sampleSize > 0 ? (
                  <div className="pt-4 pb-2">
                    <ResponseTimeGauge
                      min={responseTimes.minHours}
                      avg={responseTimes.avgHours}
                      max={responseTimes.maxHours}
                      sampleSize={responseTimes.sampleSize}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-12">
                    Insufficient timestamp data
                  </p>
                )}
              </ChartCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmendmentAnalytics;
